import clsx from 'clsx'
import { Form as FormikForm, Formik, useField } from 'formik'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import moment from 'moment'
import { KTSVG } from '../../../../_metronic/helpers'
import { useLayout } from '../../../../_metronic/layout/core'
import ComponentButton from '../ComponentButton'
import ComponentDate from '../ComponentDate'
import ComponentInCreation from '../ComponentInCreation'
import ComponentSelect from '../ComponentSelect'
import ComponentPrice from '../ComponentPrice'
import ComponentInput from '../ComponentInput'
import { useIntl } from 'react-intl'
import ComponentCheckbox from '../ComponentCheckbox'
import { TComponentFilter, TComponentFilterList, TComponentFilters, TComponentFiltersCustomToggle } from './_types'
import api from '../../../api'

const APPLIED_FILTER_SKIP_KEYS = ['fieldsVisibility', 'fieldsDescriptions', 'fieldsDisabled', 'fieldsSelectLabels']

const getEmptyValueForFilter = (filter: TComponentFilter): any => {
    if (filter.type === 'list') {
        return filter.settings.is_multi ? [] : null
    }
    if (filter.type === 'checkbox') return false
    if (filter.type === 'date_range' || filter.type === 'price_range') {
        // Для date_range и price_range нужно очистить все поля
        const result: Record<string, null> = {}
        filter.settings.fields.forEach((field) => {
            result[field.recipient_property] = null
        })
        return result
    }
    if (filter.type === 'date' || filter.type === 'integer' || filter.type === 'price') return null
    return null
}

const isFilterValueEmpty = (value: any, filter: TComponentFilter): boolean => {
    if (value === null || value === undefined) return true
    if (filter.type === 'checkbox') return value !== true
    if (filter.type === 'list' && filter.settings.is_multi) {
        return !Array.isArray(value) || value.length === 0
    }
    if (filter.type === 'date_range' || filter.type === 'price_range') {
        // Для date_range и price_range проверяем, что хотя бы одно поле заполнено
        if (!filter.settings.fields || filter.settings.fields.length === 0) return true
        return filter.settings.fields.every((field) => {
            const fieldValue = value?.[field.recipient_property]
            if (!fieldValue) return true
            // Проверяем строковые значения
            if (typeof fieldValue === 'string') return fieldValue.trim() === ''
            // Проверяем числовые значения
            if (typeof fieldValue === 'number') return false
            // Проверяем объекты с полем value (как в ComponentDate)
            if (typeof fieldValue === 'object' && fieldValue !== null) {
                const rawValue = fieldValue.value ?? fieldValue
                return !rawValue || (typeof rawValue === 'string' && rawValue.trim() === '')
            }
            return false
        })
    }
    if (typeof value === 'string') return value.trim() === ''
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object' && value !== null && (value.value === null || value.value === undefined || value.value === '')) return true
    return false
}

const formatFilterDisplayValue = (value: any, filter: TComponentFilter): string => {
    if (filter.type === 'checkbox') return filter.title ?? ''
    if (filter.type === 'list') {
        const list = filter.settings.list ?? []
        const resolve = (v: string | number) => {
            const item = list.find((i) => String(i.value) === String(v))
            return item ? item.title : String(v)
        }
        if (Array.isArray(value)) return value.map(resolve).join(', ')
        return value != null ? resolve(value) : ''
    }
    if (filter.type === 'date_range') {
        // Для date_range форматируем диапазон из двух полей
        if (!filter.settings.fields || filter.settings.fields.length === 0) return ''
        const startField = filter.settings.fields.find((f) => f.recipient_property.includes('start') || f.title.toLowerCase().includes('от'))
        const endField = filter.settings.fields.find((f) => f.recipient_property.includes('end') || f.title.toLowerCase().includes('до'))

        const startValue = startField ? value?.[startField.recipient_property] : null
        const endValue = endField ? value?.[endField.recipient_property] : null

        const formatDate = (dateValue: any) => {
            if (!dateValue) return ''
            const raw = dateValue?.value ?? dateValue
            if (!raw) return ''
            return moment(raw).format('DD.MM.YYYY')
        }

        const start = formatDate(startValue)
        const end = formatDate(endValue)

        if (start && end) return `${start} – ${end}`
        if (start) return `от ${start}`
        if (end) return `до ${end}`
        return ''
    }
    if (filter.type === 'date') {
        const raw = value?.value ?? value
        if (!raw) return ''
        if (typeof raw === 'object' && raw.start != null) {
            const start = raw.start ? moment(raw.start).format('DD.MM.YYYY') : ''
            const end = raw.end ? moment(raw.end).format('DD.MM.YYYY') : ''
            return start && end ? `${start} – ${end}` : start || end
        }
        return moment(raw).format('DD.MM.YYYY')
    }
    if (filter.type === 'integer' || filter.type === 'price') return value != null ? String(value) : ''
    return value != null ? String(value) : ''
}

const formatDateValue = (dateValue: any): string => {
    if (!dateValue) return ''
    const raw = dateValue?.value ?? dateValue
    if (!raw) return ''
    return moment(raw).format('DD.MM.YYYY')
}

const getAppliedFilters = (
    data: Array<TComponentFilter>,
    filterValues: Record<string, any> | undefined
): Array<{ key: string; title: string; displayValue: string; filter: TComponentFilter }> => {
    if (!filterValues) return []
    const result: Array<{ key: string; title: string; displayValue: string; filter: TComponentFilter }> = []
    data.forEach((filter) => {
        if (filter.type === 'date_range') {
            // Для date_range создаём два отдельных чипа
            if (!filter.settings.fields || filter.settings.fields.length === 0) return

            filter.settings.fields.forEach((field) => {
                const fieldValue = filterValues[field.recipient_property]
                if (!fieldValue) return

                // Проверяем, что значение не пустое
                const raw = fieldValue?.value ?? fieldValue
                if (!raw || (typeof raw === 'string' && raw.trim() === '')) return

                const displayValue = formatDateValue(fieldValue)
                if (!displayValue) return

                // Создаём отдельный date фильтр для каждого поля
                const dateFilter: TComponentFilter = {
                    type: 'date',
                    title: field.title || filter.title || '',
                    settings: {
                        recipient_property: field.recipient_property,
                        hook: filter.settings.hook,
                        is_visible: filter.settings.is_visible,
                        is_removable: filter.settings.is_removable,
                    },
                }

                // Формируем заголовок: "Дата от" или "Дата до"
                const fieldTitle = field.title || ''
                const baseTitle = filter.title || 'Дата'
                const chipTitle = fieldTitle ? `${baseTitle} ${fieldTitle}` : baseTitle

                result.push({
                    key: field.recipient_property,
                    title: chipTitle,
                    displayValue,
                    filter: dateFilter,
                })
            })
            return
        }

        if (filter.type === 'price_range') {
            // Для price_range создаём два отдельных чипа
            if (!filter.settings.fields || filter.settings.fields.length === 0) return

            filter.settings.fields.forEach((field) => {
                const fieldValue = filterValues[field.recipient_property]
                if (!fieldValue) return

                // Проверяем, что значение не пустое
                const raw = fieldValue?.value ?? fieldValue
                if (!raw || (typeof raw === 'string' && raw.trim() === '') || (typeof raw === 'number' && raw === 0 && fieldValue !== 0)) return

                // Форматируем цену
                const displayValue = typeof raw === 'number' ? `${raw}₽` : raw

                // Создаём отдельный price фильтр для каждого поля
                const priceFilter: TComponentFilter = {
                    type: 'price',
                    title: field.title || filter.title || '',
                    settings: {
                        recipient_property: field.recipient_property,
                        hook: filter.settings.hook,
                        is_visible: filter.settings.is_visible,
                        is_removable: filter.settings.is_removable,
                    },
                }

                // Формируем заголовок: "Сумма от" или "Сумма до"
                const fieldTitle = field.title || ''
                const baseTitle = filter.title || 'Сумма'
                const chipTitle = fieldTitle ? `${baseTitle} ${fieldTitle}` : baseTitle

                result.push({
                    key: field.recipient_property,
                    title: chipTitle,
                    displayValue,
                    filter: priceFilter,
                })
            })
            return
        }
        const key = filter.settings.recipient_property
        if (APPLIED_FILTER_SKIP_KEYS.includes(key)) return
        const value = filterValues[key]
        if (isFilterValueEmpty(value, filter)) return
        let displayValue = formatFilterDisplayValue(value, filter)
        if (filter.type === 'list' && filter.settings.is_search) {
            displayValue = filterValues.fieldsSelectLabels?.[key] ?? filterValues.fieldsDescriptions?.[key] ?? displayValue
        }
        if (!displayValue && filter.type !== 'checkbox') return
        result.push({
            key,
            title: filter.title || '',
            displayValue,
            filter,
        })
    })
    return result
}

const AppliedFiltersChips: React.FC<{
    entries: Array<{ key: string; title: string; displayValue: string; filter: TComponentFilter }>
    onRemove: (key: string) => void
}> = ({ entries, onRemove }) => {
    if (entries.length === 0) return null
    return (
        <div className='componentFilters_appliedChips'>
            {entries.map(({ key, title, displayValue, filter }) => {
                // Проверяем параметр is_removable из settings (по умолчанию true - кнопка отображается)
                const isRemovable = filter.settings?.is_removable !== false

                return (
                    <span key={key} className='componentFilters_appliedChip'>
                        <span className='componentFilters_appliedChipLabel'>
                            {title ? `${title}: ` : ''}{displayValue}

                        </span>
                        {isRemovable && (
                            <button
                                type='button'
                                className='componentFilters_appliedChipRemove'
                                onClick={() => onRemove(key)}
                                aria-label='Удалить фильтр'
                            >
                                <KTSVG
                                    path='/media/icons/duotune/general/gen034.svg'
                                    className='svg-icon-muted me-0'
                                />
                            </button>
                        )}
                    </span>
                )
            })}
        </div>
    )
}

const useToggle = () => {
    const [show, setShow] = useState(false)
    const handleToggle = useCallback((nextShow: boolean, meta: any) => {
        const isSomeOfPortalContainers = Boolean(
            meta.originalEvent.target.closest('.componentDate') ||
            meta.originalEvent.target.closest('.componentSelect__menu')
        )
        if (meta.source === 'rootClose' && isSomeOfPortalContainers) {
            return
        } else {
            setShow(nextShow)
        }
    }, [])
    return { show, handleToggle }
}

const ComponentFilter = React.memo<
    TComponentFilter & { className?: string; custom_format?: boolean; is_checkboxOption?: boolean }
>((props) => {
    const {
        type,
        settings,
        placeholder,
        data_type = 'string',
        title,
        className = '',
        custom_format,
        is_checkboxOption,
    } = props
    switch (type) {
        case 'date':
            return (
                <ComponentDate
                    className={className}
                    article={settings.recipient_property}
                    field_type={type}
                    placeholder={placeholder}
                    hook={settings.hook}
                    onBlurSubmit
                    custom_format={custom_format}
                />
            )
        case 'list':
            return (
                <ComponentSelect
                    article={settings.recipient_property}
                    data_type={(data_type || 'string') as 'string' | 'integer' | 'boolean' | 'array'}
                    placeholder={placeholder}
                    list={settings.list ?? []}
                    isMulti={Boolean(settings.is_multi)}
                    prefix={className}
                    isClearable={settings.is_clearable === false ? settings.is_clearable : true}
                    search={settings.is_search ? settings.donor_object : undefined}
                    object={settings.donor_object}
                    select={settings.donor_property_title}
                    select_menu={settings.select_menu}
                    hook={settings.hook}
                    is_checkboxOption={settings.is_checkboxOption}
                    showAllTags={Boolean(settings.is_showAllTags)}
                    onChangeSubmit
                />
            )
        case 'integer':
            return (
                <ComponentInput
                    className={className}
                    article={settings.recipient_property}
                    field_type='integer'
                    placeholder={placeholder}
                    hook={settings.hook}
                    onBlurSubmit
                />
            )
        case 'price':
            return (
                <ComponentPrice
                    className={className}
                    article={settings.recipient_property}
                    data_type='integer'
                    placeholder={placeholder}
                    hook={settings.hook}
                    onBlurSubmit
                />
            )
        case 'checkbox':
            return (
                <ComponentCheckbox
                    article={settings.recipient_property}
                    label={title}
                    className={className}
                    hook={settings.hook}
                    onChangeSubmit
                />
            )
        case 'date_range':
            return (
                <div className={`componentFilters_dateRange ${className}`}>
                    {settings.fields?.map((field, index) => (
                        <ComponentDate
                            key={field.recipient_property}
                            className={className}
                            article={field.recipient_property}
                            field_type='date'
                            placeholder={field.placeholder || field.title}
                            hook={settings.hook}
                            onBlurSubmit
                            custom_format={custom_format}
                        />
                    ))}
                </div>
            )
        case 'price_range':
            return (
                <div className={`componentFilters_priceRange ${className}`}>
                    {settings.fields?.map((field, index) => (
                        <ComponentPrice
                            key={field.recipient_property}
                            className={className}
                            article={field.recipient_property}
                            data_type='integer'
                            placeholder={field.placeholder || field.title}
                            hook={settings.hook}
                            onBlurSubmit
                        />
                    ))}
                </div>
            )
        default:
            return <ComponentInCreation type={type} />
    }
})
const ComponentFilterContainer = React.memo<
    TComponentFilter & {
        containerClassName?: string
        className?: string
        showTitle?: boolean
        custom_format?: boolean
    }
>((props) => {
    const [{ value: filtersVisibility }] = useField(`fieldsVisibility`)

    // Для date_range и price_range проверяем видимость через первое поле
    let isFieldVisible = true
    if (props.type === 'date_range' || props.type === 'price_range') {
        const firstField = props.settings.fields?.[0]
        if (firstField) {
            isFieldVisible = filtersVisibility[firstField.recipient_property] !== false
        }
    } else if ('recipient_property' in props.settings) {
        const recipientProperty = (props.settings as { recipient_property: string }).recipient_property
        isFieldVisible = filtersVisibility[recipientProperty] !== false
    }

    const resolvedContainerClassName = props.containerClassName ?? ''
    const resolvedClassName = props.className ?? ''

    if (!isFieldVisible) {
        return null
    }

    return (
        <div className={resolvedContainerClassName}>
            {props.type !== 'checkbox' && props.showTitle ? (
                <label className='form-label fw-bold'>{props.title ?? ''}</label>
            ) : null}
            <ComponentFilter
                {...props}
                placeholder={props.title ?? ''}
                className={resolvedClassName}
                custom_format={props.custom_format}
            />
        </div>
    )
})

const getFilterKey = (filter: TComponentFilter) => {
    if (filter.type === 'date_range' || filter.type === 'price_range') {
        const firstField = filter.settings.fields?.[0]
        return `${filter.type}-${firstField?.recipient_property || 'unknown'}`
    }
    if ('recipient_property' in filter.settings) {
        return `${filter.type}-${filter.settings.recipient_property}`
    }
    return `${filter.type}-unknown`
}

// Разбивает date_range и price_range на два отдельных фильтра

const ComponentFiltersCustomToggle: React.FC<TComponentFiltersCustomToggle> = React.forwardRef(
    ({ children, onClick }, ref) => {
        const { config } = useLayout()
        const daterangepickerButtonClass = config.app?.toolbar?.fixed?.desktop
            ? 'btn-light'
            : 'btn-color-gray-700 btn-active-color-primary'
        return (
            <a
                href='#'
                className={clsx(
                    'btn btn-sm btn-flex fw-bold componentFilters_dropdown',
                    daterangepickerButtonClass
                )}
                //@ts-ignore
                ref={ref}
                onClick={(e) => {
                    e.preventDefault()
                    onClick(e)
                }}
            >
                <KTSVG
                    path='/media/icons/duotune/general/gen031.svg'
                    className='svg-icon-6 svg-icon-muted me-1'
                />
                {children}
            </a>
        )
    }
)

const ComponentFiltersCustomMinimizeToggle: React.FC<TComponentFiltersCustomToggle> =
    React.forwardRef(({ children, onClick }, ref) => {
        const { config } = useLayout()
        const daterangepickerButtonClass = config.app?.toolbar?.fixed?.desktop
            ? 'btn-light'
            : 'btn-color-gray-700 btn-active-color-primary'
        return (
            <a
                href='#'
                className={clsx(
                    'btn btn-sm btn-flex fw-bold componentFilters_dropdownMinimize',
                    daterangepickerButtonClass
                )}
                //@ts-ignore
                ref={ref}
                onClick={(e) => {
                    e.preventDefault()
                    onClick(e)
                }}
            >
                <KTSVG
                    path='/media/crm/icons/dots.svg'
                    className='svg-icon-6 svg-icon-muted me-0'
                />
            </a>
        )
    })

const ComponentFiltersDropdown: React.FC<TComponentFilters> = ({ fullWidth = true, ...props }) => {
    const intl = useIntl()

    const initialValues = useMemo(() => {
        const initialSettings: { [key: string]: any } = {
            fieldsVisibility: {},
            fieldsDescriptions: {},
            fieldsSelectLabels: {},
        }
        // Инициализируем fieldsVisibility для всех полей
        props.data.forEach((filter) => {
            if (filter.type === 'date_range' || filter.type === 'price_range') {
                filter.settings.fields?.forEach((field) => {
                    if (!(field.recipient_property in initialSettings.fieldsVisibility)) {
                        initialSettings.fieldsVisibility[field.recipient_property] =
                            filter.settings.is_visible === false ? false : true
                    }
                })
            } else if ('recipient_property' in filter.settings) {
                if (!(filter.settings.recipient_property in initialSettings.fieldsVisibility)) {
                    initialSettings.fieldsVisibility[filter.settings.recipient_property] =
                        filter.settings.is_visible === false ? false : true
                }
            }
        })
        return Object.assign({}, initialSettings, props.filterValues ?? {})
    }, [props.data, props.filterValues])

    const appliedEntries = useMemo(
        () => getAppliedFilters(props.data, props.filterValues),
        [props.data, props.filterValues]
    )

    const handleRemoveChip = useCallback(
        (key: string) => {
            // Проверяем, является ли это поле частью date_range или price_range
            const rangeFilter = props.data.find((f) =>
                (f.type === 'date_range' || f.type === 'price_range') &&
                f.settings.fields?.some((field) => field.recipient_property === key)
            )

            if (rangeFilter) {
                // Для date_range и price_range очищаем только конкретное поле
                props.handleChange({
                    ...(props.filterValues ?? {}),
                    [key]: null,
                })
                return
            }

            // Для обычных фильтров
            const filter = props.data.find((f) => {
                if ('recipient_property' in f.settings) {
                    return f.settings.recipient_property === key
                }
                return false
            })
            if (!filter) return

            const emptyValue = getEmptyValueForFilter(filter)
            props.handleChange({
                ...(props.filterValues ?? {}),
                [key]: emptyValue,
            })
        },
        [props.data, props.filterValues, props.handleChange]
    )

    const { show, handleToggle } = useToggle()

    return (
        <div className={`componentFilters_dropdownContainer${fullWidth ? ' fullWidth' : ''}`}>
            <div className='componentFilters_appliedChipsWrap'>
                <AppliedFiltersChips entries={appliedEntries} onRemove={handleRemoveChip} />
            </div>
            <div className='componentFilters_dropdownButtonWrap'>
                <Dropdown show={show} onToggle={handleToggle}>
                    <Dropdown.Toggle
                        as={
                            props.minimize
                                ? ComponentFiltersCustomMinimizeToggle
                                : ComponentFiltersCustomToggle
                        }
                        id='dropdown-custom-components'
                    >
                        {intl.formatMessage({ id: 'FILTER.DROPDOWN_TITLE' })}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <div className='w-250px w-md-350px componentFilters_dropdownMenu'>
                            <div className='px-7 py-5'>
                                <div className='fs-5 text-dark fw-bolder'>
                                    {intl.formatMessage({ id: 'FILTER.DROPDOWN_TITLE' })}
                                </div>
                            </div>
                            <div className='separator' />
                            <div className='p-5'>
                                <Formik
                                    enableReinitialize
                                    initialValues={initialValues}
                                    onSubmit={(values) => props.handleChange(values)}
                                >
                                    {({ handleSubmit, handleReset }) => {
                                        const handleFormReset = () => {
                                            if (props.filterValues) {
                                                return props.handleReset()
                                            } else {
                                                handleReset()
                                                return handleSubmit()
                                            }
                                        }
                                        return (
                                            <FormikForm>
                                                <div className='componentFilters_dropdownFiltersContainer'>
                                                    {props.data.map((filter: TComponentFilter) => (
                                                        <ComponentFilterContainer
                                                            key={getFilterKey(filter)}
                                                            containerClassName={`componentFilters_dropdownFilter col-md-${filter.settings.size
                                                                ? filter.settings.size * 3
                                                                : 12
                                                                }`}
                                                            {...filter}
                                                            showTitle
                                                        />
                                                    ))}
                                                </div>
                                                <ComponentButton
                                                    type='submit'
                                                    settings={{
                                                        title: intl.formatMessage({
                                                            id: 'FILTER.CLEAR_BUTTON',
                                                        }),
                                                        background: 'dark',
                                                        icon: '',
                                                    }}
                                                    customHandler={handleFormReset}
                                                    disabled={props.isInitials}
                                                />
                                            </FormikForm>
                                        )
                                    }}
                                </Formik>
                            </div>
                        </div>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </div>
    )
}

const ComponentFiltersBlock: React.FC<TComponentFilters> = (props) => {
    const intl = useIntl()
    const initialValues = useMemo(() => props.filterValues ?? {}, [props.filterValues])
    const { show, handleToggle } = useToggle()
    return (
        <div className='componentFilters_block'>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={(values) => props.handleChange(values)}
            >
                {({ handleSubmit, handleReset }) => {
                    const showClearFilterButton = !props.isInitials

                    const handleFormReset = () => {
                        if (props.filterValues) {
                            return props.handleReset()
                        } else {
                            handleReset()
                            return handleSubmit()
                        }
                    }

                    return (
                        <FormikForm className='componentFilters_blockForm'>
                            <div className='componentFilters_blockContainer'>
                                {props.data.map((filter) => (
                                    <div
                                        key={getFilterKey(filter)}
                                        className='componentFilters_blockFilter'
                                    >
                                        <ComponentFilter
                                            {...filter}
                                            placeholder={filter.title ?? ''}
                                            custom_format
                                        />
                                    </div>
                                ))}
                            </div>
                            {showClearFilterButton ? (
                                <ComponentButton
                                    type='submit'
                                    className='componentFilters_blockButton'
                                    settings={{
                                        title: intl.formatMessage({ id: 'FILTER.CLEAR_BUTTON' }),
                                        background: 'light',
                                        icon: 'trash',
                                    }}
                                    customHandler={handleFormReset}
                                />
                            ) : null}
                            <div className='componentFilters_hiddenDropdown'>
                                <Dropdown show={show} onToggle={handleToggle}>
                                    <Dropdown.Toggle
                                        as={ComponentFiltersCustomToggle}
                                        id='dropdown-custom-components'
                                    >
                                        {intl.formatMessage({ id: 'FILTER.DROPDOWN_TITLE' })}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <div className='w-250px w-md-300px componentFilters_dropdownMenu'>
                                            <div className='px-7 py-5'>
                                                <div className='fs-5 text-dark fw-bolder'>
                                                    {intl.formatMessage({
                                                        id: 'FILTER.DROPDOWN_TITLE',
                                                    })}
                                                </div>
                                            </div>
                                            <div className='separator'></div>
                                            <div className='px-7 py-5'>
                                                {props.data.map((filter: TComponentFilter) => (
                                                    <div
                                                        key={getFilterKey(filter)}
                                                        className='componentFilters_dropdownFilter'
                                                    >
                                                        {filter.type !== 'checkbox' ? (
                                                            <label className='form-label fw-bold'>
                                                                {filter.title ?? ''}
                                                            </label>
                                                        ) : null}
                                                        <ComponentFilter {...filter} />
                                                    </div>
                                                ))}
                                                <ComponentButton
                                                    type='submit'
                                                    settings={{
                                                        title: intl.formatMessage({
                                                            id: 'FILTER.CLEAR_BUTTON',
                                                        }),
                                                        background: 'dark',
                                                        icon: '',
                                                    }}
                                                    customHandler={handleFormReset}
                                                    disabled={props.isInitials}
                                                />
                                            </div>
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </FormikForm>
                    )
                }}
            </Formik>
        </div>
    )
}

const ComponentFiltersString: React.FC<TComponentFilters> = (props) => {
    const intl = useIntl()

    const initialValues = useMemo(() => {
        const initialSettings: { [key: string]: any } = {
            fieldsVisibility: {},
            fieldsDescriptions: {},
            fieldsSelectLabels: {},
        }
        // Инициализируем fieldsVisibility для всех полей
        props.data.forEach((filter) => {
            if (filter.type === 'date_range' || filter.type === 'price_range') {
                filter.settings.fields?.forEach((field) => {
                    if (!(field.recipient_property in initialSettings.fieldsVisibility)) {
                        initialSettings.fieldsVisibility[field.recipient_property] =
                            filter.settings.is_visible === false ? false : true
                    }
                })
            } else if ('recipient_property' in filter.settings) {
                if (!(filter.settings.recipient_property in initialSettings.fieldsVisibility)) {
                    initialSettings.fieldsVisibility[filter.settings.recipient_property] =
                        filter.settings.is_visible === false ? false : true
                }
            }
        })
        return Object.assign({}, initialSettings, props.filterValues ?? {})
    }, [props.data, props.filterValues])

    const { show, handleToggle } = useToggle()

    return (
        <div className='componentFilters_string'>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={(values) => props.handleChange(values)}
            >
                {({ handleSubmit, handleReset }) => {
                    const showClearFilterButton = !props.isInitials

                    const handleFormReset = () => {
                        if (props.filterValues) {
                            return props.handleReset()
                        } else {
                            handleReset()
                            return handleSubmit()
                        }
                    }

                    return (
                        <FormikForm className='componentFilters_stringForm'>
                            {props.data.map((filter) => (
                                <ComponentFilterContainer
                                    key={getFilterKey(filter)}
                                    containerClassName='componentFilters_stringFilterContainer'
                                    className='componentFilters_stringFilter'
                                    custom_format={props.custom_format}
                                    {...filter}
                                />
                            ))}
                            {showClearFilterButton ? (
                                <ComponentButton
                                    type='submit'
                                    className='componentFilters_stringButton'
                                    settings={{
                                        title: intl.formatMessage({ id: 'FILTER.CLEAR_BUTTON' }),
                                        background: 'light',
                                        icon: 'trash',
                                    }}
                                    defaultLabel='icon'
                                    customHandler={handleFormReset}
                                />
                            ) : null}
                            <div className='componentFilters_hiddenDropdown'>
                                <Dropdown show={show} onToggle={handleToggle}>
                                    <Dropdown.Toggle
                                        as={ComponentFiltersCustomToggle}
                                        id='dropdown-custom-components'
                                    >
                                        {intl.formatMessage({ id: 'FILTER.DROPDOWN_TITLE' })}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <div className='w-250px w-md-300px componentFilters_dropdownMenu'>
                                            <div className='px-7 py-5'>
                                                <div className='fs-5 text-dark fw-bolder'>
                                                    {intl.formatMessage({
                                                        id: 'FILTER.DROPDOWN_TITLE',
                                                    })}
                                                </div>
                                            </div>
                                            <div className='separator'></div>
                                            <div className='px-7 py-5'>
                                                {props.data.map((filter: TComponentFilter) => (
                                                    <ComponentFilterContainer
                                                        key={getFilterKey(filter)}
                                                        containerClassName={`componentFilters_dropdownFilter col-md-${filter.settings.size
                                                            ? filter.settings.size * 3
                                                            : 12
                                                            }`}
                                                        {...filter}
                                                        showTitle
                                                    />
                                                ))}
                                                <ComponentButton
                                                    type='submit'
                                                    settings={{
                                                        title: intl.formatMessage({
                                                            id: 'FILTER.CLEAR_BUTTON',
                                                        }),
                                                        background: 'dark',
                                                        icon: '',
                                                    }}
                                                    customHandler={handleFormReset}
                                                    disabled={props.isInitials}
                                                />
                                            </div>
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </FormikForm>
                    )
                }}
            </Formik>
        </div>
    )
}
const useResolvedSearchLabels = (
    data: TComponentFilter[],
    filterValues?: Record<string, any>
): Record<string, string> => {
    const [resolvedLabels, setResolvedLabels] = useState<Record<string, string>>({})
    const prevFetchedRef = useRef<Record<string, any>>({})

    const searchFilters = useMemo(
        () => data.filter((f): f is TComponentFilterList => f.type === 'list' && Boolean(f.settings.is_search)),
        [data]
    )

    useEffect(() => {
        if (!filterValues || !searchFilters.length) return

        searchFilters.forEach(async (filter) => {
            const key = filter.settings.recipient_property
            const value = filterValues[key]
            if (value == null || value === '' || prevFetchedRef.current[key] === value) return

            try {
                const ids: any[] = Array.isArray(value) ? value : [value]
                const results = await Promise.all(
                    ids.map((id) =>
                        api<Array<any>>(filter.settings.donor_object, 'get', {
                            context: { block: 'select' },
                            id,
                            select: (filter.settings as any).select ?? filter.settings.donor_property_title,
                            select_menu: filter.settings.select_menu,
                        }).then((res) => res.data[0])
                    )
                )
                const label = results.filter(Boolean).map((r: any) => r.title).join(', ')
                if (label) {
                    prevFetchedRef.current[key] = value
                    setResolvedLabels((prev) => ({ ...prev, [key]: label }))
                }
            } catch {}
        })
    }, [filterValues, searchFilters])

    return resolvedLabels
}

const normalizeFilters = (data: any): TComponentFilter[] => {
    if (Array.isArray(data)) {
        return data
    }

    if (data && typeof data === 'object') {
        return Object.values(data) as TComponentFilter[]
    }

    return []
}

const ComponentFilters: React.FC<TComponentFilters> = (props) => {
    const data = useMemo(() => normalizeFilters(props.data), [props.data])
    const resolvedLabels = useResolvedSearchLabels(data, props.filterValues)

    const augmentedFilterValues = useMemo(() => {
        if (!Object.keys(resolvedLabels).length) return props.filterValues
        return {
            ...props.filterValues,
            fieldsDescriptions: {
                ...props.filterValues?.fieldsDescriptions,
            },
            fieldsSelectLabels: {
                ...props.filterValues?.fieldsSelectLabels,
                ...resolvedLabels,
            },
        }
    }, [props.filterValues, resolvedLabels])

    const filtersCount = data.length
    const visibleFilters = data.filter((item) => item.settings?.is_visible !== false)

    if (visibleFilters.length === 0) {
        return null
    }

    const augmentedProps = { ...props, data, filterValues: augmentedFilterValues }

    if (props.type === 'string') {
        return <ComponentFiltersString {...augmentedProps} />
    } else if (filtersCount <= 3 && props.type === 'block') {
        return <ComponentFiltersBlock {...augmentedProps} />
    } else {
        return <ComponentFiltersDropdown {...augmentedProps} />
    }
}

export default ComponentFilters
